import { Component, h, Prop, Event, EventEmitter, State, Element, Host } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { IconName } from '../icon/icons';
import { IconSize } from '../icon/icon';

@Component({
  tag: 'anchor-accordion-item',
  styleUrl: 'accordion-item.scss',
  shadow: true,
})
export class AccordionItem {
  @Element() element: HTMLElement;

  /**
   *  Give this instance of accordion item a custom ID, or keep the default.
   */
  @Prop({}) uuid: string = uuidv4();

  /**
   * tabindex is a reserved name so we use tabi which will apply the tabindex attribute
   * Default value should work fine so don't change this unless you have a really good reason to. Set to -1 to disable tabbing.
   */
  @Prop({
    attribute: 'tab-index',
  })
  tabi: number = 0;

  @Prop() userControlledContent: boolean = false;

  /**
   * Whether the accordion-item is expanded
   */
  @Prop({ reflect: true, mutable: true }) isExpanded: boolean = false;

  /**
   * Style of the accordion-item. You should ideally not mix these if using inside an accordion.
   */
  @Prop() variant: 'default' | 'box' = 'default';

  /**
   * Text to display in the header
   */

  @Prop() label = undefined;

  /**
   * Icon to display in the header.
   * Note that the icon will be rotated when accordion-item is expanded.
   */
  @Prop() icon: IconName = 'chevron-down';

  /**
   * Size of the chevron.
   */
  @Prop() iconSize: IconSize = 'medium';

  /**
   * Stroke size for the chevron.
   */
  @Prop() iconStroke = 2;

  /**
   * Vertical alignment of the icon
   */
  @Prop({ mutable: true, reflect: true }) iconVerticalAlign?: 'start' | 'center' | 'end' = 'center';

  /**
   * Make the accordion open up on top, instead of on bottom
   */
  @Prop() reverse?: boolean = false;

  /**
   * Heading level for the accordion item. This is used to determine the heading tag for the accordion item. If not set, it will default to a div.
   *
   */
  @Prop({ reflect: true, mutable: true }) headingLevel: number = 0;

  /**
   * Event dispatched when accordion-item expands or contracts
   * `{querySelector}.addEventListener('toggled', ({detail}) => {console.log('expanded:', detail.value)})`
   */
  @Event() toggled: EventEmitter<{ value: boolean }>;

  private onToggled = () => {
    this.isExpanded = !this.isExpanded;
    this.toggled.emit({ value: this.isExpanded });
  };

  @State() isFocused: boolean;

  render() {
    const HeadingTag = this.headingLevel ? `h${this.headingLevel}` : 'div';

    return (
      <Host>
        <div
          id={this.uuid}
          class={{
            'anchor-c-accordion-item': true,
            'expanded': this.isExpanded,
            'focused': this.isFocused,
            'box': this.variant === 'box',
            'reverse': this.reverse,
          }}
        >
          <HeadingTag class={`header header--vertical-align-${this.iconVerticalAlign}`} part="accordion-header">
            <button
              id={`${this.uuid}__button`}
              class="header-content"
              onClick={() => this.onToggled()}
              onFocus={() => {
                this.isFocused = true;
              }}
              onBlur={() => {
                this.isFocused = false;
              }}
              onMouseDown={() =>
                setTimeout(() => {
                  this.isFocused = false;
                }, 1)
              }
              tabindex={this.tabi}
              aria-expanded={this.isExpanded ? 'true' : 'false'}
              aria-controls={`${this.uuid}__content-container`}
            >
              {this.label ? <span>{this.label}</span> : <slot name="label" />}
              <slot name="action" />
              <anchor-icon name={this.icon} size={this.iconSize} stroke={this.iconStroke} />
            </button>
          </HeadingTag>

          <div
            id={`${this.uuid}__content-container`}
            class={{
              'content-container': true,
              'not-user-controlled-content': !this.userControlledContent,
            }}
            aria-labelledby={`${this.uuid}__button`}
            role="region"
          >
            <slot name="content" />
            <slot />
          </div>
        </div>
      </Host>
    );
  }
}
